/**
 * Sky environment map.
 *
 * Howard, on the night: "I can't see anything in nighttime. Need to add a moon so you
 * can see reflections of things and not be pitch black."
 *
 * A directional moon alone gives a standard material one small specular dot. What makes
 * water and wet surfaces read at night is an environment to reflect, so this captures
 * the sky dome itself into a cubemap and hands it to the scene. It shares the sky's
 * material, so the moon in the reflection is the same moon that is in the sky, and it is
 * only re-captured when the light has actually moved — a cubemap render per frame would
 * cost more than the whole rest of the frame.
 */
import * as THREE from 'three'

/** Re-capture once the light has changed by this much, as a 0..1 daylight fraction. */
const RECAPTURE_STEP = 0.035

export class SkyEnvironment {
  private readonly pmrem: THREE.PMREMGenerator
  private readonly captureScene = new THREE.Scene()
  private target: THREE.WebGLRenderTarget | null = null
  private lastDaylight = Number.NaN

  constructor(renderer: THREE.WebGLRenderer, skyMesh: THREE.Mesh) {
    this.pmrem = new THREE.PMREMGenerator(renderer)
    this.pmrem.compileEquirectangularShader()
    // A second dome sharing the sky's material and uniforms, so it is never out of step
    // with the sky the player is looking at.
    const twin = new THREE.Mesh(new THREE.SphereGeometry(600, 32, 20), skyMesh.material)
    twin.frustumCulled = false
    this.captureScene.add(twin)
  }

  /** The captured cubemap, for a material that wants its own scalable reflection. */
  get texture(): THREE.Texture | null {
    return this.target?.texture ?? null
  }

  /** Refresh the reflection when the light has moved enough to be worth it. */
  update(scene: THREE.Scene, daylight: number): boolean {
    if (Math.abs(daylight - this.lastDaylight) < RECAPTURE_STEP) return false
    this.lastDaylight = daylight
    // The far plane matters: fromScene defaults to 100, and the sky dome has a radius of
    // 600, so the default clips the entire sky away and captures an empty black cubemap.
    const next = this.pmrem.fromScene(this.captureScene, 0, 1, 1200)
    this.target?.dispose()
    this.target = next
    scene.environment = next.texture
    return true
  }

  dispose(): void {
    this.target?.dispose()
    this.pmrem.dispose()
  }
}
